import type { components } from "@carwash/api-types";
import { config } from "./config";
import { ApiError } from "./apiError";
import { captureApiFailure } from "./sentry";
import type { Membership } from "./auth/scope";
import { getAuthSession, requireScope } from "./auth/session";

export type { Membership };

type Schemas = components["schemas"];

export type CheckInServiceBody = Schemas["CheckInServiceBody"];
export type StartServiceBody = Schemas["StartServiceBody"];
export type MarkServiceReadyBody = Schemas["MarkServiceReadyBody"];
export type CollectServiceBody = Schemas["CollectServiceBody"];
export type CancelServiceBody = Schemas["CancelServiceBody"];
export type ReorderServiceBody = Schemas["ReorderServiceBody"];
export type CheckInServiceResponse = Schemas["CheckInServiceResponse"];

// Display name rides on the mutation so the optimistic card can show it. It is
// stripped before POST — it is not a check-in attribute.
export type CheckInVariables = CheckInServiceBody & { serviceName: string };

export type CatalogItem = Schemas["ServiceTypeResponse"];
export type UpsertCatalogItemRequest = Schemas["UpsertServiceType"];
export type Site = Schemas["SiteResponse"];
export type AcceptedInvite = Schemas["AcceptInviteResponse"];
export type CreateInviteRequest = Schemas["CreateInvite"];
export type CreateInviteResponse = Schemas["CreateInviteResponse"];
export type CreateBusinessRequest = Schemas["CreateBusiness"];
export type CreateBusinessResponse = Schemas["CreateBusinessResponse"];
export type AddSiteRequest = Schemas["AddSite"];
export type UpdateSiteRequest = Schemas["UpdateSite"];

// Board cards. ServicePro has no queue-list document, so this stays a local view.
export type BoardStatus =
  | "Queued"
  | "InProgress"
  | "ReadyForCollection"
  | "Collected"
  | "Cancelled"
  | "Abandoned";

export interface WashVisit {
  id: string;
  siteId: string;
  status: BoardStatus;
  registration: string;
  make: string | null;
  model: string | null;
  colour: string | null;
  size: "Small" | "Medium" | "Large" | "ExtraLarge";
  serviceName: string;
  quotedPrice: number;
  phoneNumber: string;
  queuePosition: number;
  inferred: boolean;
  capturedAt: string;
  startedAt: string | null;
  readyAt: string | null;
  highlight?: boolean;
}

type OpenServiceCard = Schemas["OpenServiceCard"];

const VEHICLE_SIZES = ["Small", "Medium", "Large", "ExtraLarge"] as const;

function toWashVisit(card: OpenServiceCard): WashVisit {
  const size = VEHICLE_SIZES.find((known) => known === card.attributes?.size) ?? "Medium";
  return {
    id: card.id,
    siteId: card.siteId,
    status: card.status as BoardStatus,
    registration: card.attributes.registration ?? "",
    make: card.attributes.make ?? null,
    model: card.attributes.model ?? null,
    colour: card.attributes.colour ?? null,
    size,
    serviceName: card.serviceName,
    quotedPrice: Number(card.quotedPrice),
    phoneNumber: card.mobile,
    queuePosition: Number(card.queuePosition),
    inferred: false,
    capturedAt: card.capturedAt,
    startedAt: null,
    readyAt: null,
    highlight: card.highlight === true,
  };
}

export interface StaffMe {
  email: string;
  displayName: string;
  memberships: Membership[];
}

export interface InvitePreview {
  businessName: string;
  email: string;
  role: string;
  siteId: string | null;
  siteName: string | null;
  expiresAt: string;
}

export interface InviteSummary {
  id: string;
  email: string;
  role: string;
  siteId: string | null;
  siteName: string | null;
  createdAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  acceptedByEmail: string | null;
  inviteUrl: string;
}

export interface WashReport {
  mode: string;
  siteId: string | null;
  timeZone: string;
  asOf: string;
  periods: {
    key: string;
    from: string;
    to: string;
    current: { washCount: number | string; quotedRand: number | string };
    previous: { washCount: number | string; quotedRand: number | string };
    delta: {
      washCount: number | string;
      quotedRand: number | string;
      washCountPercent: number | string | null;
      quotedRandPercent: number | string | null;
    };
    seriesGrain: string;
    series: { bucket: string; washCount: number | string; quotedRand: number | string }[];
  }[];
}

export { ApiError };

// Tenant-scoped path prefixes. Business and site now come from the SESSION (resolved from
// GET /auth/staff/me at sign-in), not from build-time VITE_BUSINESS_ID/VITE_SITE_ID — one
// deployment serves every business.
const base = () => `${config.apiBase}/businesses/${requireScope().businessId}`;
const siteBase = () => `${base()}/sites/${requireScope().siteId}`;

// Every command body carries a clientCommandId (UUIDv7); queries do not.
function commandIdOf(body: unknown): string | undefined {
  return body && typeof body === "object" && "clientCommandId" in body &&
    typeof (body as { clientCommandId: unknown }).clientCommandId === "string"
    ? (body as { clientCommandId: string }).clientCommandId
    : undefined;
}

async function once<T>(url: string, method: string, body: unknown, token: string | null): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!res.ok) {
    let detail = `Request failed (${res.status})`;
    try {
      const problem = await res.json();
      if (problem?.detail) detail = problem.detail;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

// The single choke point: token attach, the 401 refresh-retry, and Sentry capture all live
// here, so no call site can forget any of them. Mirrors customer-web's apiFetch.
async function send<T>(url: string, method: "GET" | "POST" | "PUT", body?: unknown): Promise<T> {
  // Read fresh from the live Firebase session on EVERY call — never stored, never cached.
  // A queued command replayed hours later therefore re-authenticates as whoever is signed in
  // now, rather than carrying a stale token from when it was queued.
  const auth = getAuthSession();
  const token = auth ? await auth.getIdToken() : null;

  try {
    return await once<T>(url, method, body, token);
  } catch (e) {
    // One forced-refresh retry on 401 — an expired ID token is the ordinary case on a tablet
    // that has been asleep. A 403 is deliberately NOT retried: that is an authorization
    // answer about this business/site, and a fresh token would say exactly the same thing.
    if (e instanceof ApiError && e.status === 401 && auth) {
      try {
        return await once<T>(url, method, body, await auth.getIdToken(true));
      } catch (retryError) {
        // A second 401 means the session itself is dead, not the token: sign out so the app
        // falls back to the sign-in screen rather than looping on an unusable session.
        if (retryError instanceof ApiError && retryError.status === 401) await auth.signOut();
        captureApiFailure(retryError, { path: url, clientCommandId: commandIdOf(body) });
        throw retryError;
      }
    }
    captureApiFailure(e, { path: url, clientCommandId: commandIdOf(body) });
    throw e;
  }
}

export const api = {
  // Sign-in reads: these RESOLVE the scope, so they must not require one. staffMe carries no
  // business at all; listSites takes the business explicitly, because the site picker runs
  // after the business is chosen but before the scope is complete.
  staffMe: () => send<StaffMe>(`${config.apiBase}/auth/staff/me`, "GET"),
  listSites: (businessId: string) => send<Site[]>(`${config.apiBase}/businesses/${businessId}/sites`, "GET"),

  // Owner reporting: businessId is passed explicitly because ReportingScreen is prop-drilled
  // (same as Settings), not session-scoped via base()/requireScope().
  getWashReport: (businessId: string, mode: "calendar" | "trailing", siteId?: string) => {
    const q = new URLSearchParams({ mode });
    if (siteId) q.set("siteId", siteId);
    return send<WashReport>(
      `${config.apiBase}/businesses/${businessId}/reports/washes?${q}`,
      "GET",
    );
  },

  // Self-serve onboarding: neither call can use base()/requireScope() — there is no scope yet,
  // that's exactly what these two calls are creating. businessId is threaded explicitly instead.
  createBusiness: (body: CreateBusinessRequest) =>
    send<CreateBusinessResponse>(`${config.apiBase}/businesses`, "POST", body),
  addSite: (businessId: string, body: AddSiteRequest) =>
    send<Site>(`${config.apiBase}/businesses/${businessId}/sites`, "POST", body),

  // Business-wide catalog administration. Distinct from listCatalog() below, which returns the
  // EFFECTIVE catalog for the current site — these are the raw business-default items the settings
  // editor and the wizard's completeness check need, and they take businessId explicitly because
  // the wizard runs before a scope exists.
  listCatalogItems: (businessId: string) =>
    send<CatalogItem[]>(`${config.apiBase}/businesses/${businessId}/service-types`, "GET"),
  upsertCatalogItem: (businessId: string, serviceTypeId: string, body: UpsertCatalogItemRequest) =>
    send<CatalogItem>(`${config.apiBase}/businesses/${businessId}/service-types/${serviceTypeId}`, "PUT", body),
  updateSite: (businessId: string, siteId: string, body: UpdateSiteRequest) =>
    send<Site>(`${config.apiBase}/businesses/${businessId}/sites/${siteId}`, "PUT", body),

  // Invite flow: neither call has (or may require) a business/site scope — preview runs signed
  // out, accept runs signed in but before any membership exists. The token travels in the BODY,
  // never the path, so it cannot reach an access log or Sentry's endpoint tag.
  previewInvite: (token: string) =>
    send<InvitePreview>(`${config.apiBase}/auth/staff/invites/preview`, "POST", { token }),
  acceptInvite: (token: string) =>
    send<AcceptedInvite>(`${config.apiBase}/auth/staff/invites/accept`, "POST", { token }),

  // Owner-only in practice: /businesses/{id}/invites is a business-wide route, so the server
  // refuses a SiteManager. The team screen's role check is UX on top of that, not the control.
  listInvites: () => send<InviteSummary[]>(`${base()}/invites`, "GET"),
  createInvite: (body: CreateInviteRequest) => send<CreateInviteResponse>(`${base()}/invites`, "POST", body),

  listActiveVisits: async () => {
    const cards = await send<OpenServiceCard[]>(`${siteBase()}/services`, "GET");
    return cards.map(toWashVisit);
  },
  listCatalog: () => send<CatalogItem[]>(`${siteBase()}/service-types`, "GET"),
  checkInService: (body: CheckInServiceBody) =>
    send<CheckInServiceResponse>(`${siteBase()}/services`, "POST", body),
  startService: (serviceId: string, body: StartServiceBody) =>
    send<unknown>(`${base()}/services/${serviceId}/start`, "POST", body),
  markServiceReady: (serviceId: string, body: MarkServiceReadyBody) =>
    send<unknown>(`${base()}/services/${serviceId}/ready`, "POST", body),
  collectService: (serviceId: string, body: CollectServiceBody) =>
    send<unknown>(`${base()}/services/${serviceId}/collect`, "POST", body),
  cancelService: (serviceId: string, body: CancelServiceBody) =>
    send<unknown>(`${base()}/services/${serviceId}/cancel`, "POST", body),
  reorderService: (serviceId: string, body: ReorderServiceBody) =>
    send<unknown>(`${base()}/services/${serviceId}/reorder`, "POST", body),
};
